"use client";

/**
 * Product window + frame-level overlays — shared by desktop {@link HeroAppWindow}
 * and mobile {@link HeroMobileWindow}. Same tree, same proportions; mobile
 * wraps this in {@link ResponsiveScale}.
 */
import { ld } from "@/lib/theme/light-dark";
import type { CSSProperties, ReactNode } from "react";

import { MODULE_HEIGHT, WINDOW_SHADOW } from "../hero-tokens";
import type { HeroModuleKey } from "../hero-types";
import { AppTopBar } from "./app-top-bar";
import { CallBanner } from "./call-banner";
import { MODULE_REGISTRY } from "./module-registry";
import { InboxUiProvider } from "./modules/inbox-ui-context";

export type HeroProductWindowProps = {
  readonly active: HeroModuleKey;
  readonly setActive: (key: HeroModuleKey) => void;
  readonly inCall: boolean;
  readonly setInCall: (value: boolean) => void;
  /**
   * Mobile only: in-flow spacer below the window so overlays hanging off the
   * bottom edge (phones, logo rows, seals) are not clipped.
   */
  readonly mobileOverlayBleedPx?: number;
  /**
   * Mobile only: window corner radius in BASE px. Mobile scales this tree down,
   * so the radius must be pre-multiplied to land on the frame's intended screen
   * radius. Desktop leaves it unset and uses the native 16.
   */
  readonly windowRadiusPx?: number;
};

export function HeroProductWindow(props: HeroProductWindowProps): ReactNode {
  const entry = MODULE_REGISTRY[props.active];
  const isMobil = props.active === "mobil";
  const bleedPx = props.mobileOverlayBleedPx ?? 0;
  const moduleProps = {
    active: props.active,
    setActive: props.setActive,
    inCall: props.inCall,
    setInCall: props.setInCall,
  };

  const windowShellStyle: CSSProperties = {
    position: "relative",
    zIndex: 1,
    borderRadius: props.windowRadiusPx ?? 16,
    overflow: "hidden",
    boxShadow: isMobil ? "none" : WINDOW_SHADOW,
    // Transparent shell so AppTopBar frosted glass shows the aurora behind it.
    // Module body below stays #fff — only the bookmark bar is see-through.
    background: "transparent",
  };

  const windowShellStyleContent: ReactNode = (
    <div style={windowShellStyle}>
      {entry.chromeless === true ? (
        <entry.Component {...moduleProps} />
      ) : (
        <>
          <AppTopBar active={props.active} setActive={props.setActive} />
          {props.active === "telefon" && props.inCall ? (
            <CallBanner onHangup={() => props.setInCall(false)} />
          ) : null}
          {entry.customBody === true ? (
            <entry.Component {...moduleProps} />
          ) : (
            <div
              style={{
                height: MODULE_HEIGHT,
                overflow: "hidden",
                background: ld("#fff", "#181b1f"),
              }}
            >
              <entry.Component {...moduleProps} />
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <div style={{ position: "relative", width: "100%" }}>
        {props.active === "inbox" ? (
          <InboxUiProvider>
            {windowShellStyleContent}
            {entry.Overlay ? <entry.Overlay /> : null}
          </InboxUiProvider>
        ) : (
          <>
            {windowShellStyleContent}
            {entry.Overlay ? <entry.Overlay /> : null}
          </>
        )}
      </div>

      {bleedPx > 0 ? (
        <div
          aria-hidden="true"
          style={{ height: bleedPx, pointerEvents: "none" }}
        />
      ) : null}
    </>
  );
}
